import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import moment from "moment-jalaali";
import { toPersianDigits } from "../../utils/persianNumbers";
import "./Calendar.css";

const Calendar = ({ onDateChange, initialSelectedDate }) => {
  const { t, i18n } = useTranslation();
  const isPersian = i18n.language === 'fa';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialSelectedDate) {
      const [year, month, day] = initialSelectedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date();
  });
  const lastProcessedDate = useRef(null);

  useEffect(() => {
    if (initialSelectedDate && initialSelectedDate !== lastProcessedDate.current) {
      const [year, month, day] = initialSelectedDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      const formattedDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      lastProcessedDate.current = initialSelectedDate;
      onDateChange(formattedDate);
      setCurrentDate(date);
    }
  }, [initialSelectedDate, onDateChange]);

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    2
  ).getDay();

  const getMonthName = (monthIndex) => {
    const monthKeys = [
      'month_january',
      'month_february',
      'month_march',
      'month_april',
      'month_may',
      'month_june',
      'month_july',
      'month_august',
      'month_september',
      'month_october',
      'month_november',
      'month_december',
    ];
    return t(monthKeys[monthIndex]);
  };

  const getPersianDate = (date) => {
    const m = moment(date);
    const monthIndex = m.jMonth() + 1;
    return {
      day: m.jDate(),
      month: monthIndex,
      year: m.jYear(),
      monthName: t(`persian_month_${monthIndex}`),
    };
  };

  const getDayName = (dayIndex) => {
    const dayKeys = ['day_sat', 'day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri'];
    return t(dayKeys[dayIndex]);
  };

  const gregorianDays = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  const handleDateClick = (day) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(date);

    // Fix: Adjust for timezone to ensure correct date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const formattedDate = `${year}-${month}-${dayStr}`;

    onDateChange(formattedDate);
  };

  const getPersianMonthsForGregorianMonth = (date) => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const firstPersianDate = moment(firstDayOfMonth);
    const lastPersianDate = moment(lastDayOfMonth);

    const months = new Set();
    const firstMonthIndex = firstPersianDate.jMonth() + 1;
    months.add({
      month: firstMonthIndex,
      year: firstPersianDate.jYear(),
      name: t(`persian_month_${firstMonthIndex}`),
    });

    if (firstPersianDate.jMonth() !== lastPersianDate.jMonth()) {
      const lastMonthIndex = lastPersianDate.jMonth() + 1;
      months.add({
        month: lastMonthIndex,
        year: lastPersianDate.jYear(),
        name: t(`persian_month_${lastMonthIndex}`),
      });
    }

    return Array.from(months);
  };

  const renderDays = () => {
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const persianDate = getPersianDate(date);
      const isSelected =
        selectedDate && date.toDateString() === selectedDate.toDateString();
      const isToday = date.toDateString() === new Date().toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? "selected" : ""} ${
            isToday ? "today" : ""
          }`}
          onClick={() => handleDateClick(day)}
        >
          <div className="gregorian-date">{isPersian ? toPersianDigits(day) : day}</div>
          <div className="persian-date">{isPersian ? toPersianDigits(persianDate.day) : persianDate.day}</div>
        </div>
      );
    }
    return days;
  };

  const activePersianMonths = getPersianMonthsForGregorianMonth(currentDate);

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button onClick={handlePrevMonth}>&lt;</button>
        <div className="calendar-title">
          <h2>
            {getMonthName(currentDate.getMonth())} {isPersian ? toPersianDigits(currentDate.getFullYear()) : currentDate.getFullYear()}
          </h2>
          <div className="persian-months">
            {activePersianMonths.map((month, index) => (
              <h3 key={index} className="persian-title">
                {index < activePersianMonths.length - 1 && " / "}
                {month.name} {isPersian ? toPersianDigits(month.year) : month.year}
              </h3>
            ))}
          </div>
        </div>
        <button onClick={handleNextMonth}>&gt;</button>
      </div>
      <div className="calendar-days-header">
        {gregorianDays.map((day, index) => (
          <div key={day} className="day-name">
            <div>{getDayName(index)}</div>
          </div>
        ))}
      </div>
      <div className="calendar-grid">{renderDays()}</div>
    </div>
  );
};

export default Calendar;
